'use client';

import { usePathname } from 'next/navigation';
import { memo, type ReactNode, Suspense } from 'react';

import { TitleBar } from '@air/plugin-ui';
import { ErrorBoundary } from '@air/utils-error';

import { ClientBootstrap } from '~/app/_components/ClientBootstrap';
import Providers from '~/app/providers';
import { BrowserGate, type BrowserGateProps } from '~/components/BrowserGate';
import { PluginInitializationListener } from '~/components/PluginInitializationListener';
import { BROWSER_TAB_ROUTES } from '~/constants/routes';

export type AppShellProps = {
  children: ReactNode;
} & Pick<BrowserGateProps, 'isProduction'>;

/**
 * Client boundary that wraps the entire app tree. Exists so the root
 * layout can remain a Server Component despite the tree depending on
 * client-only packages (@air/plugin-ui, @air/utils-error) whose dist
 * entries don't carry a 'use client' directive — importing them
 * directly from a Server Component fails the Next build with a
 * "useEffect outside a Client Component" error.
 *
 * Everything below this point runs on the client.
 */
export const AppShell = memo(({ children, isProduction }: AppShellProps) => {
  const pathname = usePathname();
  const isBrowserTabRoute = pathname !== null && BROWSER_TAB_ROUTES.has(pathname);

  return (
    <ErrorBoundary>
      {/* Suspense wraps BrowserGate because it calls `useSearchParams` (to
          honor the `?previewInBrowser=1` bypass). Without the boundary, every
          page bails out of static prerender and Next's build fails with
          `useSearchParams() should be wrapped in a suspense boundary`. */}
      <Suspense fallback={null}>
        <BrowserGate isProduction={isProduction}>
          <Providers>
            <ClientBootstrap />
            <PluginInitializationListener />
            {isBrowserTabRoute ? (
              children
            ) : (
              <div className="flex h-screen flex-col">
                {/* UXP panels manage their own chrome — no close button needed */}
                <TitleBar />
                <div className="flex-1 overflow-auto">{children}</div>
              </div>
            )}
          </Providers>
        </BrowserGate>
      </Suspense>
    </ErrorBoundary>
  );
});

AppShell.displayName = 'AppShell';
